import React, { useMemo } from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import { humanEUR, pct, signedPct } from "./format";

function safeDiv(a, b) {
  return b ? a / b : 0;
}

const columns = [
  { Header: "Category", accessor: "label" },
  { Header: "Spend", accessor: "spend", align: "right" },
  { Header: "Share", accessor: "share", align: "right" },
  { Header: "MoM", accessor: "mom", align: "right" },
];

/**
 * CloudAiSplitBlock - Cloud vs AI split of total spend, each with its own MoM.
 *
 * GET /api/v1/financial-control/summary -> { summary: { kpis: { spend_vs_forecast, ai_share } } }
 * cloud = actual - ai_share.cost (per the Task-0 binding table). The backend only reports
 * AI's own MoM (ai_share.mom_pct); Cloud's MoM is derived the same way from the previous
 * month's figures (prev_month_actual - ai_share.prev_cost) so both rows are comparable -
 * documented binding default (no backend "cloud" KPI exists to read it from directly).
 */
const CloudAiSplitBlock = ({ title = "Cloud vs. AI", period }) => {
  const { data, isLoading, error } = useBlockData("/api/v1/financial-control/summary", {
    params: period ? { period } : undefined,
  });

  const spend = data?.summary?.kpis?.spend_vs_forecast;
  const ai = data?.summary?.kpis?.ai_share;

  const rows = useMemo(() => {
    const actual = spend?.actual ?? 0;
    const prevActual = spend?.prev_month_actual ?? 0;
    const aiCost = ai?.cost ?? 0;
    const prevAiCost = ai?.prev_cost ?? 0;

    const cloud = actual - aiCost;
    const cloudPrev = prevActual - prevAiCost;
    const cloudShare = actual ? safeDiv(cloud, actual) : null;
    const cloudMoM = cloudPrev ? safeDiv(cloud - cloudPrev, cloudPrev) : null;

    return [
      {
        id: "cloud",
        label: "Cloud",
        spend: humanEUR(cloud),
        share: cloudShare == null ? "—" : pct(cloudShare),
        mom: cloudMoM == null ? "—" : signedPct(cloudMoM),
      },
      {
        id: "ai",
        label: "AI",
        spend: humanEUR(aiCost),
        share: ai?.pct == null ? "—" : pct(ai.pct),
        mom: ai?.mom_pct == null ? "—" : signedPct(ai.mom_pct),
      },
    ];
  }, [spend, ai]);

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error}>
      <FlexTable columns={columns} data={rows} getRowId={(row) => row.id} />
    </BaseBlock>
  );
};

CloudAiSplitBlock.propTypes = {
  title: PropTypes.string,
  period: PropTypes.string,
};

export default CloudAiSplitBlock;
